import React, { useState, useEffect, useRef } from "react";
import { Xmark, ArrowUpFromSquare, Copy, Check} from "@gravity-ui/icons";
import { usePostHog } from "posthog-js/react";

// Update props to accept shareUrl
const ShareModal = ({ isOpen, onClose, trackInfo, shareUrl }) => {
  const [copied, setCopied] = useState(false);
  const modalRef = useRef(null); // Add ref for the modal content
  const posthog = usePostHog();
  
  // Remove local URL generation logic
  // const trackId = trackInfo?.id || 'demo-track';
  // const shareUrl = `${window.location.origin}/share/${trackId}`;
  
  // Log the received URL
  useEffect(() => {
    console.log("ShareModal received URL:", shareUrl);
  }, [shareUrl]);

  // Handle click outside of modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add event listener when modal is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleCopyLink = () => {
    posthog.capture("Click-Copy-For-Discord-ShareTrackModal",{is_user_logged_in: true})
    if (shareUrl) { // Only copy if URL exists
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };

  // --- Social Sharing Logic --- 
  const baseText = "Check out my track from Wubble: ";
  const encodedShareUrl = shareUrl ? encodeURIComponent(shareUrl) : "";
  const encodedTextWithUrl = shareUrl ? encodeURIComponent(`${baseText}${shareUrl}`) : "";
  const encodedBaseText = encodeURIComponent(baseText);

  const twitterShareUrl = shareUrl ? `https://twitter.com/intent/tweet?url=${encodedShareUrl}&text=${encodedBaseText}` : "#";
  const facebookShareUrl = shareUrl ? `https://www.facebook.com/sharer/sharer.php?u=${encodedShareUrl}` : "#"; // Primarily shares URL
  const linkedinShareUrl = shareUrl ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodedShareUrl}` : "#"; // Primarily shares URL
  const telegramShareUrl = shareUrl ? `https://t.me/share/url?url=${encodedShareUrl}&text=${encodedBaseText}` : "#";
  const whatsappShareUrl = shareUrl ? `https://api.whatsapp.com/send?text=${encodedTextWithUrl}` : "#";
  // Discord doesn't have a standard web share intent, copy is the primary method

  const openShareWindow = (url) => {
    if (url && url !== "#") {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  // --- End Social Sharing Logic ---

  return (
    <div 
      className={`fixed inset-0 z-50 p-4 flex items-center justify-center ${isOpen ? "visible " : "invisible"}`}
      style={{ background: 'rgba(0, 0, 0, 0.8)' }}
    >
      <div 
        ref={modalRef} 
        className="flex flex-col w-full max-w-md bg-[#1d1b1d] border border-solid border-[#3c393c] rounded-lg"
      >
        {/* Header */}
        <div className="flex items-center justify-between pl-3 pb-2 pt-2 pr-2">
          <div className="flex items-center gap-2">
            <ArrowUpFromSquare className="w-5 h-5" />
            <h2 className="text-lg font-normal">Share track</h2>
          </div>
          <button 
            onClick={() => {
              onClose();
              posthog.capture("Click-Close-ShareTrackModal",{is_user_logged_in: true});
            }}
            className="flex items-center justify-center hover:bg-white/5 rounded-full h-8 w-8 text-white/50 hover:text-white"
          >
            <Xmark className="w-4 h-4"/>
          </button>
        </div>
        <div className="w-full border-t border-t-[#2d2a2d]"></div>

        {/* URL Input */}
        <div className="p-4 pb-5 w-full">
          <div className="flex items-center w-full">
            <div className="flex w-full justify-between rounded-full pl-3 pr-0.5 items-center h-9 text-sm font-extralight text-white/40 truncate border border-[#4E4A4F]">
              {/* Display the passed shareUrl or a placeholder */}
              <span className="w-full max-w-80 truncate">
                {shareUrl || 'Generating link...'}
              </span>
              <button 
                onClick={handleCopyLink}
                disabled={!shareUrl} // Disable copy if no URL
                className="flex items-center justify-center hover:bg-white/10 hover:text-white bg-white/5 h-8 w-8 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-white"/>
                ) : (
                  <Copy className="w-4 h-4"/>
                )}
              </button>
            </div>
            
          </div>
        </div>

        {/* Social Buttons */}
        <div className="flex justify-between pt-0 px-4 pb-5 w-full">
          {/* Facebook */}
          <a href={facebookShareUrl} target="_blank" rel="noopener noreferrer" className={`p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full ${!shareUrl ? 'opacity-50 pointer-events-none' : 'transition duration-500'}`}>
            <button onClick={() => posthog.capture("Click-Facebook-ShareTrackModal",{is_user_logged_in: true})} className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
              <img src="/Facebook.svg" alt="Share on Facebook"/>
            </button>
          </a>
          {/* Twitter (X) */}
          <a href={twitterShareUrl} target="_blank" rel="noopener noreferrer" className={`p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full ${!shareUrl ? 'opacity-50 pointer-events-none' : 'transition duration-500'}`}>
            <button onClick={() => posthog.capture("Click-Twitter-ShareTrackModal",{is_user_logged_in: true})} className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
              <img src="/X.svg" alt="Share on X"/>
            </button>
          </a>
          {/* LinkedIn */}
          <a href={linkedinShareUrl} target="_blank" rel="noopener noreferrer" className={`p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full ${!shareUrl ? 'opacity-50 pointer-events-none' : 'transition duration-500'}`}>
            <button onClick={() => posthog.capture("Click-Linkedin-ShareTrackModal",{is_user_logged_in: true})} className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
              <img src="/Linkedin.svg" alt="Share on LinkedIn"/>
            </button>
          </a>
          {/* Telegram */}
          <a href={telegramShareUrl} target="_blank" rel="noopener noreferrer" className={`p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full ${!shareUrl ? 'opacity-50 pointer-events-none' : 'transition duration-500'}`}>
            <button onClick={() => posthog.capture("Click-Telegram-ShareTrackModal",{is_user_logged_in: true})} className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
              <img src="/Telegram.svg" alt="Share on Telegram"/>
            </button>
          </a>
          {/* WhatsApp */}
          <a href={whatsappShareUrl} target="_blank" rel="noopener noreferrer" className={`p-px h-10 w-10 flex items-center justify-center hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] rounded-full ${!shareUrl ? 'opacity-50 pointer-events-none' : 'transition duration-500'}`}>
            <button onClick={() => posthog.capture("Click-Whatsapp-ShareTrackModal",{is_user_logged_in: true})} className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
              <img src="/Whatsapp.svg" alt="Share on WhatsApp"/>
            </button>
          </a>
          {/* Discord - Keep as simple button or remove, as no standard web share */} 
          <div className={`p-px h-10 w-10 flex items-center justify-center rounded-full ${!shareUrl ? 'opacity-50 pointer-events-none' : 'hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] transition duration-500'}`}>
            <button  title="Copy link to share on Discord" onClick={handleCopyLink} className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2c2a2c] hover:bg-[#3C393C]">
               {/* Use Copy icon for Discord as placeholder for copy action */}
               <Copy className={`w-4 h-4 ${copied ? 'text-white' : ''}`}/> 
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;